import { createRouter } from '@tanstack/react-router'
import { setupRouterSsrQueryIntegration } from '@tanstack/react-router-ssr-query'
import { QueryClient } from '@tanstack/react-query'
import { routeTree } from './routeTree.gen'

function GlobalPendingComponent() {
  return (
    <div className="fixed inset-0 z-[100] pointer-events-none">
      <div className="absolute top-0 left-0 right-0 h-1 bg-primary/20 overflow-hidden">
        <div
          className="h-full bg-primary"
          style={{
            width: '30%',
            animation: 'progress-indeterminate 1.5s ease-in-out infinite',
          }}
        />
      </div>
    </div>
  )
}

export const getRouter = () => {
  /*
   * ═══════════════════════════════════════════════════════════════════════════
   * 🔴 O QueryClient NASCE AQUI — um por chamada de `getRouter()`.
   *
   * Ele morava em `routes/__root.tsx` como `export const queryClient = new
   * QueryClient(...)`, módulo-level. **No servidor isso é um cliente só para
   * TODOS os alunos que o container atende**: o Bun serve todo mundo no mesmo
   * processo, e `getRouter()` roda por REQUISIÇÃO enquanto o módulo é avaliado
   * uma vez.
   *
   * O que atravessava: o cache do aluno A ainda vivo (`gcTime` era de 5
   * minutos) quando o SSR do aluno B roda — e o payload desidratado que o
   * `setupRouterSsrQueryIntegration` embute no HTML sai da MESMA instância.
   * Progresso, perfil, XP e posts são todos dado pessoal.
   *
   * ⚠️ Este era o ÚNICO dos seis apps com o client de módulo. Os outros cinco já
   * criavam dentro do `getRouter()` — o comentário do `conteudo/src/router.tsx`
   * explica exatamente o porquê, e o `student` foi o que ficou para trás.
   *
   * No browser, `getRouter()` roda uma vez no boot: o cliente segue com um
   * único QueryClient pela sessão da aba, como antes.
   * ═══════════════════════════════════════════════════════════════════════════
   */
  const queryClient = new QueryClient({
    defaultOptions: {
      queries: {
        /*
         * 🔴 RETENÇÃO ZERO — o `gcTime` era a metade que faltava.
         *
         * O `gcTime` daqui era `5 * 60 * 1000`, e `staleTime: 0` não cobre isso:
         * ele só vale para o refetch do `useQuery`. Quem serve dado velho no
         * carregamento é o loader — `ensureQueryData` devolve o que estiver no
         * cache **sem revalidar** (`revalidateIfStale` é `false` por padrão, e
         * nenhum loader do repo o passa). Basta a entrada AINDA EXISTIR.
         *
         * Zero nos dois é a política do produto enquanto o cache não for
         * religado de propósito, e vale para os SEIS apps. Guarda:
         * `src/router.test.ts`.
         */
        staleTime: 0,
        gcTime: 0,
        refetchOnWindowFocus: false,
        refetchOnReconnect: false,
        retry: (failureCount, error: any) => {
          if (error?.status === 401 || error?.value?.status === 401) return false
          return failureCount < 2
        },
      },
    },
  })

  const router = createRouter({
    routeTree,
    context: { queryClient },
    scrollRestoration: true,
    defaultPreloadStaleTime: 0,
    defaultViewTransition: true,
    defaultPendingComponent: GlobalPendingComponent,
    defaultPendingMs: 150,
    defaultPendingMinMs: 200,
  })

  // `wrapQueryClient` no default (true): a integração envolve o app com
  // `<QueryClientProvider>` usando o client DESTE router. Era `false`, e o
  // provider era escrito à mão no `__root.tsx` com o client de módulo.
  setupRouterSsrQueryIntegration({
    router,
    queryClient,
  })

  return router
}
